import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ISkillReferenceDTO } from '@sharedInterfaces/ISkill';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AboutMeTab from './tabs/AboutMeTab';
import CompetenciesTab from './tabs/CompetenciesTab';
import MyCertificatesTab from './tabs/MyCertificatesTab';
import MyProfileOverview from './tabs/MyProfileOverview';
import SkillCheckerTab from './tabs/SkillCheckerTab';
import SkillManagerTab from './tabs/SkillManagerTab';
import SuperPowersTab from './tabs/SuperPowersTab';
import T4loMissionsTab from './tabs/T4loMissionsTab';

// Tab configuration
const createTabConfig = () => [
    { label: 'Übersicht', icon: getAppIcon(EIconCategory.OVERVIEW), component: MyProfileOverview },
    { label: 'About Me', icon: getAppIcon(EIconCategory.ABOUTME), component: AboutMeTab },
    { label: 'SuperPowers', icon: getAppIcon(EIconCategory.SUPERPOWERS), component: SuperPowersTab },
    { label: 'Zertifikate', icon: getAppIcon(EIconCategory.CERTIFICATES), component: MyCertificatesTab },
    { label: 'Kompetenzen', icon: getAppIcon(EIconCategory.COMPETENCES), component: CompetenciesTab },
    { label: 'Skill Manager', icon: getAppIcon(EIconCategory.SKILLS), component: SkillManagerTab },
    { label: 'Skill Checker', icon: getAppIcon(EIconCategory.DEVELOP), component: SkillCheckerTab },
    { label: 'T4lo Missions', icon: getAppIcon(EIconCategory.MISSIONS), component: T4loMissionsTab },
];

interface MyDevCardProps {
    employee: Employee;
    onTabChange?: (tabIndex: number) => void;
}

function MyDevCard({ employee, onTabChange }: MyDevCardProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(0);
    const tabs = createTabConfig();

    // Check for tab parameter in URL
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const tabIndex = parseInt(tabParam, 10);
            if (tabIndex >= 0 && tabIndex < tabs.length) {
                setActiveTab(tabIndex);
            }
        }
    }, [searchParams, tabs.length]);

    // SuperPowers state - lifted up to share between components
    const skills = employee.skills || [];
    const [currentSuperPowers, setCurrentSuperPowers] = useState<ISkillReferenceDTO[]>(() =>
        skills
            .filter((skill) => skill.level >= 3)
            .sort((a, b) => b.level - a.level)
            .slice(0, 6),
    );

    const ActiveComponent = tabs[activeTab]?.component || tabs[0].component;

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        onTabChange?.(newValue);
    };

    // Listen for navigation events from recommendation clicks
    useEffect(() => {
        const handleNavigateToTab = (event: CustomEvent) => {
            setActiveTab(event.detail.tabIndex);
        };

        window.addEventListener('navigateToTab', handleNavigateToTab as EventListener);
        return () => {
            window.removeEventListener('navigateToTab', handleNavigateToTab as EventListener);
        };
    }, []);

    return (
        <Box
            sx={{
                background: theme.palette.background.paper,
                borderRadius: 3,
                boxShadow: theme.shadows[1],
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                overflow: 'hidden',
                width: '100%',
            }}
        >
            {/* Tab Navigation */}
            <Box sx={{ px: { xs: 0.5, lg: 3 }, mt: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : 'fullWidth'}
                    scrollButtons={isMobile ? 'auto' : false}
                    allowScrollButtonsMobile={isMobile}
                    sx={{
                        '& .MuiTabs-indicator': {
                            backgroundColor: theme.palette.primary.main,
                        },
                        '& .MuiTab-root': {
                            color: theme.palette.text.secondary,
                            fontWeight: 400,
                            textTransform: 'none',
                            '&.Mui-selected': {
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                            },
                        },
                        '& .MuiTabs-scrollButtons': {
                            color: theme.palette.text.secondary,
                            '&.Mui-disabled': {
                                opacity: 0.3,
                            },
                        },
                    }}
                >
                    {tabs.map((tab) => (
                        <Tab key={tab.label} label={tab.label} icon={tab.icon} />
                    ))}
                </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: { xs: 2, md: 3, lg: '24px 32px' }, pt: { xs: 3, md: 4 }, px: { xs: 2, md: 3, lg: '32px' } }}>
                {activeTab === 0 ? (
                    <ActiveComponent
                        employee={employee}
                        onTabChange={setActiveTab}
                        currentSuperPowers={currentSuperPowers}
                        setCurrentSuperPowers={setCurrentSuperPowers}
                    />
                ) : activeTab === 1 ? (
                    <ActiveComponent employee={employee} onTabChange={setActiveTab} />
                ) : activeTab === 2 ? (
                    <ActiveComponent
                        employee={employee}
                        onTabChange={setActiveTab}
                        currentSuperPowers={currentSuperPowers}
                        setCurrentSuperPowers={setCurrentSuperPowers}
                    />
                ) : (
                    <ActiveComponent employee={employee} onTabChange={setActiveTab} />
                )}
            </Box>
        </Box>
    );
}

export default MyDevCard;
